import { create } from "zustand";

/**
 * Draft filter selection, shared between the compact bar in the layout and the
 * panel inside the drawer. Kept in zustand rather than component state so both
 * mount points read the same draft and the draft survives closing the drawer.
 *
 * This store owns state and persistence only. Applying the filter (redux
 * dispatch + navigation) lives in useQuestionFilters, which needs React
 * context.
 */

export type FilterDimension = "years" | "agencies" | "subjects" | "keywords";

export const FILTER_DIMENSIONS: FilterDimension[] = [
  "years",
  "agencies",
  "subjects",
  "keywords",
];

/** Prefix shown on an active-filter token so it reads out of context (§23.9). */
export const DIMENSION_LABEL: Record<FilterDimension, string> = {
  years: "Ano",
  agencies: "Instituição",
  subjects: "Matéria",
  keywords: "Assunto",
};

export type AnswerStatus = "unanswered" | "wrong" | "all";

export interface FilterSelection {
  years: string[];
  agencies: string[];
  subjects: string[];
  keywords: string[];
  answerStatus: AnswerStatus;
}

export interface ActiveFilterToken {
  dimension: FilterDimension;
  dimensionLabel: string;
  value: string;
}

export const EMPTY_SELECTION: FilterSelection = {
  years: [],
  agencies: [],
  subjects: [],
  keywords: [],
  answerStatus: "unanswered",
};

const STORAGE_KEYS = {
  years: "filters.years",
  agencies: "filters.agencies",
  subjects: "filters.subjects",
  keywords: "filters.keywords",
  answerStatus: "filters.answerStatus",
  /** written once the "YYYY / Nome" → years + agencies migration has run */
  migrated: "filters.schema",
} as const;

const SCHEMA_VERSION = "2";

function readList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function writeList(key: string, values: string[]) {
  if (values.length === 0) localStorage.removeItem(key);
  else localStorage.setItem(key, JSON.stringify(values));
}

const unique = (values: string[]) => Array.from(new Set(values));

/**
 * v1 stored institutions as the joined string "2025 / CBMGO" in filters.agencies.
 * Split those into the two independent dimensions so an existing user does not
 * silently lose their filters on first load after the upgrade.
 */
function migrateLegacySelection(): { years: string[]; agencies: string[] } {
  const stored = readList(STORAGE_KEYS.agencies);
  const years: string[] = [];
  const agencies: string[] = [];

  stored.forEach((entry) => {
    const separator = entry.indexOf("/");
    if (separator === -1) {
      const trimmed = entry.trim();
      if (/^\d{4}$/.test(trimmed)) years.push(trimmed);
      else if (trimmed) agencies.push(trimmed);
      return;
    }
    const year = entry.slice(0, separator).trim();
    const name = entry.slice(separator + 1).trim();
    if (year) years.push(year);
    if (name) agencies.push(name);
  });

  return { years: unique(years), agencies: unique(agencies) };
}

function isAnswerStatus(value: string | null): value is AnswerStatus {
  return value === "unanswered" || value === "wrong" || value === "all";
}

function hydrateFromStorage(): FilterSelection {
  const alreadyMigrated = localStorage.getItem(STORAGE_KEYS.migrated) === SCHEMA_VERSION;

  let years: string[];
  let agencies: string[];

  if (alreadyMigrated) {
    years = readList(STORAGE_KEYS.years);
    agencies = readList(STORAGE_KEYS.agencies);
  } else {
    const migrated = migrateLegacySelection();
    years = migrated.years;
    agencies = migrated.agencies;
    writeList(STORAGE_KEYS.years, years);
    writeList(STORAGE_KEYS.agencies, agencies);
    localStorage.setItem(STORAGE_KEYS.migrated, SCHEMA_VERSION);
  }

  const storedStatus = localStorage.getItem(STORAGE_KEYS.answerStatus);

  return {
    years,
    agencies,
    subjects: readList(STORAGE_KEYS.subjects),
    keywords: readList(STORAGE_KEYS.keywords),
    answerStatus: isAnswerStatus(storedStatus) ? storedStatus : "unanswered",
  };
}

function persist(selection: FilterSelection) {
  writeList(STORAGE_KEYS.years, selection.years);
  writeList(STORAGE_KEYS.agencies, selection.agencies);
  writeList(STORAGE_KEYS.subjects, selection.subjects);
  writeList(STORAGE_KEYS.keywords, selection.keywords);
  localStorage.setItem(STORAGE_KEYS.answerStatus, selection.answerStatus);
  localStorage.setItem(STORAGE_KEYS.migrated, SCHEMA_VERSION);
}

export function countSelection(selection: FilterSelection): number {
  return FILTER_DIMENSIONS.reduce((total, dim) => total + selection[dim].length, 0);
}

export function selectionTokens(selection: FilterSelection): ActiveFilterToken[] {
  return FILTER_DIMENSIONS.flatMap((dimension) =>
    selection[dimension].map((value) => ({
      dimension,
      dimensionLabel: DIMENSION_LABEL[dimension],
      value,
    })),
  );
}

export function sameSelection(a: FilterSelection, b: FilterSelection): boolean {
  if (a.answerStatus !== b.answerStatus) return false;
  return FILTER_DIMENSIONS.every((dim) => {
    if (a[dim].length !== b[dim].length) return false;
    const other = new Set(b[dim]);
    return a[dim].every((value) => other.has(value));
  });
}

/** How many individual values differ between draft and applied. */
export function countPendingChanges(
  draft: FilterSelection,
  applied: FilterSelection,
): number {
  const dimensionDiff = FILTER_DIMENSIONS.reduce((total, dim) => {
    const appliedSet = new Set(applied[dim]);
    const draftSet = new Set(draft[dim]);
    const added = draft[dim].filter((v) => !appliedSet.has(v)).length;
    const removed = applied[dim].filter((v) => !draftSet.has(v)).length;
    return total + added + removed;
  }, 0);

  return dimensionDiff + (draft.answerStatus === applied.answerStatus ? 0 : 1);
}

interface QuestionFiltersState {
  draft: FilterSelection;
  applied: FilterSelection;
  hydrated: boolean;
  applying: boolean;
  panelOpen: boolean;

  hydrate: () => void;
  toggle: (dimension: FilterDimension, value: string) => void;
  remove: (dimension: FilterDimension, value: string) => void;
  setAnswerStatus: (status: AnswerStatus) => void;
  clearAll: () => void;
  /** Drop stored keywords that no longer exist in the current context. */
  pruneKeywords: (available: string[]) => void;
  markApplied: () => void;
  setApplying: (applying: boolean) => void;
  setPanelOpen: (open: boolean) => void;
}

const useQuestionFiltersStore = create<QuestionFiltersState>((set, get) => ({
  draft: EMPTY_SELECTION,
  applied: EMPTY_SELECTION,
  hydrated: false,
  applying: false,
  panelOpen: false,

  hydrate: () => {
    if (get().hydrated) return;
    const selection = hydrateFromStorage();
    // Restored filters are already in effect on the server-side query, so draft
    // and applied start in sync — the bar must not open showing pending changes.
    set({ draft: selection, applied: selection, hydrated: true });
  },

  toggle: (dimension, value) =>
    set((state) => {
      const current = state.draft[dimension];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const draft = { ...state.draft, [dimension]: next };
      persist(draft);
      return { draft };
    }),

  remove: (dimension, value) =>
    set((state) => {
      const draft = {
        ...state.draft,
        [dimension]: state.draft[dimension].filter((v) => v !== value),
      };
      persist(draft);
      return { draft };
    }),

  setAnswerStatus: (answerStatus) =>
    set((state) => {
      const draft = { ...state.draft, answerStatus };
      persist(draft);
      return { draft };
    }),

  clearAll: () =>
    set(() => {
      persist(EMPTY_SELECTION);
      return { draft: EMPTY_SELECTION };
    }),

  pruneKeywords: (available) =>
    set((state) => {
      if (state.draft.keywords.length === 0) return {};
      const allowed = new Set(available);
      const keywords = state.draft.keywords.filter((k) => allowed.has(k));
      if (keywords.length === state.draft.keywords.length) return {};
      const draft = { ...state.draft, keywords };
      persist(draft);
      return { draft };
    }),

  markApplied: () => set((state) => ({ applied: state.draft })),
  setApplying: (applying) => set({ applying }),
  setPanelOpen: (panelOpen) => set({ panelOpen }),
}));

export default useQuestionFiltersStore;
